import { CountryDialCode, getCountriesDialCodes, getCountryDialCodeById } from "@/lib/db/countries-dial-codes";

const US_COUNTRY_ID = 235;

/** Dial codes sorted longest-first so multi-digit prefixes (e.g. +1684) match before +1. */
const DIAL_CODES_LONGEST_FIRST: ReadonlyArray<CountryDialCode> = [...getCountriesDialCodes()].sort(
  (a, b) => b.dial_code.length - a.dial_code.length
);

/**
 * True when a string should not be parsed as a phone number (digits in email local parts are not phones).
 * @param value - Raw field value from form JSON.
 */
export const isEmailLikeString = (value: string): boolean => value.includes("@");

/**
 * Extracts E.164 from form `response_data`-style payloads: `{ phone, countryDialCodeId }` objects or digit-like strings.
 * Skips values that look like emails so addresses are never treated as phone numbers.
 * @param responseData - `nowadays.form_responses.response_data` (or equivalent map).
 * @returns Normalized E.164 or null.
 */
export const extractE164FromFormResponseData = (
  responseData: Record<string, unknown> | null | undefined
): string | null => {
  if (!responseData) return null;

  for (const value of Object.values(responseData)) {
    if (typeof value === "object" && value !== null && "phone" in value) {
      const o = value as { phone?: string; countryDialCodeId?: number | null };
      const rawPhone = o.phone?.trim();
      if (rawPhone && !isEmailLikeString(rawPhone)) {
        const e164 = toE164(rawPhone, o.countryDialCodeId ?? null);
        if (e164) return e164;
      }
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed || isEmailLikeString(trimmed)) continue;
      const digits = trimmed.replace(/\D/g, "");
      if (digits.length >= 7 && digits.length <= 15) {
        const e164 = toE164(trimmed, null);
        if (e164) return e164;
      }
    }
  }

  return null;
};

/** A national number paired with the `countries_dial_codes` id it belongs to. */
export interface PhoneParts {
  phone: string;
  countryDialCodeId: number | null;
}

/**
 * True when the number carries its own country code: a leading `+`, or the `00`
 * international access prefix used across Europe and most of the world.
 * @param phone - Raw phone value.
 * @returns Whether the value is written in international form.
 */
export const hasExplicitCountryCode = (phone: string): boolean => /^\s*(\+|00)/.test(phone);

/**
 * Splits a phone answer into its national number and country. A number the person wrote with
 * its own country code (`+353…`, `00353…`) keeps that country, even over `countryDialCodeId`:
 * the supplied id is often just a picker's US default, and letting it win turns `+35385…`
 * into `+185…`. Anything else is returned trimmed, paired with `countryDialCodeId`.
 *
 * @param phone - Raw phone value (national digits, or a number with its own country code).
 * @param countryDialCodeId - Country chosen alongside the number, if any.
 * @returns The national number and the country it belongs to.
 */
export function splitPhoneCountry(phone: string, countryDialCodeId: number | null): PhoneParts {
  if (hasExplicitCountryCode(phone)) {
    const parsed = normalizeExistingPhone(phone);
    if (parsed.countryDialCodeId) return { phone: parsed.digits, countryDialCodeId: parsed.countryDialCodeId };
  }
  return { phone: phone.trim(), countryDialCodeId };
}

/**
 * Reads a form phone answer (the phone widget's `{ phone, countryDialCodeId }` object or a typed
 * string) as {@link PhoneParts}, keeping any country code the answer carries.
 *
 * @param answer - A `response_data` / `rsvp_data` phone value.
 * @param fallbackCountryDialCodeId - Country to pair with an answer that names none.
 * @returns The national number and its country, or null when the answer holds no phone.
 */
export function phoneAnswerToParts(answer: unknown, fallbackCountryDialCodeId: number | null): PhoneParts | null {
  if (typeof answer === "string") return answer.trim() ? splitPhoneCountry(answer, fallbackCountryDialCodeId) : null;
  if (!answer || typeof answer !== "object" || !("phone" in answer)) return null;
  const { phone, countryDialCodeId } = answer as { phone?: unknown; countryDialCodeId?: number | null };
  if (typeof phone !== "string" || !phone.trim()) return null;
  return splitPhoneCountry(phone, countryDialCodeId ?? fallbackCountryDialCodeId);
}

/**
 * Converts a phone number + country dial code ID to E.164 format. A number written with its own
 * country code (`+CC` / `00CC`) is parsed as international and the country id is ignored.
 * Returns null if the number cannot be normalized.
 */
export function toE164(phone: string, countryDialCodeId: number | null): string | null {
  const digits = phone.replace(/\D/g, "");
  if (!digits) return null;

  if (countryDialCodeId && !hasExplicitCountryCode(phone)) {
    const country = getCountryDialCodeById(countryDialCodeId);
    if (country) {
      // NANP (+1) users commonly type the leading "1" into the national field by muscle memory.
      // The total length is unambiguous (1 + 10 digits), so strip to avoid `+114086071553`.
      // Other countries have variable national lengths and national numbers can legitimately
      // start with the same digit as the dial code, so we don't auto-strip there.
      let national = digits;
      if (country.dial_code === "1" && national.length === 11 && national.startsWith("1")) {
        national = national.slice(1);
      }
      if (national.length < 4 || national.length > 15) return null;
      return `+${country.dial_code}${national}`;
    }
  }

  // No country dial code - try to parse from the original phone string
  return normalizeExistingPhone(phone).e164;
}

/**
 * Extracts the national-digit portion of a stored phone for editing — strips the
 * country dial code prefix when it matches the supplied country FK. Returns the
 * full digit string if no country is provided or the prefix doesn't match.
 *
 * @param phone - Stored phone (E.164 with `+`, or digits-only).
 * @param countryDialCodeId - FK into countries_dial_codes from the same row.
 */
export function nationalDigitsForCountry(phone: string, countryDialCodeId: number | null): string {
  const digits = phone.replace(/\D/g, "");
  if (!digits || !countryDialCodeId) return digits;
  const country = getCountryDialCodeById(countryDialCodeId);
  if (!country) return digits;
  let result = digits;
  if (result.length > country.dial_code.length && result.startsWith(country.dial_code)) {
    result = result.slice(country.dial_code.length);
  }
  // Heal legacy double-prefixed NANP rows (`+114086071553` → digits `114086071553`
  // → strip first `1` leaves `14086071553`; the remaining `1` is also a duped country code).
  if (country.dial_code === "1" && result.length === 11 && result.startsWith("1")) {
    result = result.slice(1);
  }
  return result;
}

/**
 * Checks if a country dial code ID corresponds to a US/Canada number (NANP dial code "1").
 */
export function isDomesticUS(countryDialCodeId: number | null): boolean {
  if (!countryDialCodeId) return true; // Default assumption for legacy numbers
  const country = getCountryDialCodeById(countryDialCodeId);
  if (!country) return true;
  return country.dial_code === "1";
}

/**
 * Parses a legacy phone number string (without a separate country dial code ID)
 * into digits and a best-guess country dial code ID.
 */
export function normalizeExistingPhone(phone: string): {
  digits: string;
  countryDialCodeId: number | null;
  e164: string | null;
} {
  // "00" is the international access prefix outside NANP, so "00353…" means "+353…".
  const trimmed = phone.trim().replace(/^00/, "+");

  if (trimmed.startsWith("+")) {
    // Try matching longest dial codes first (e.g., +1684 before +1)
    for (const country of DIAL_CODES_LONGEST_FIRST) {
      const prefix = `+${country.dial_code}`;
      if (trimmed.startsWith(prefix)) {
        const remaining = trimmed.slice(prefix.length).replace(/\D/g, "");
        if (remaining.length >= 4) {
          return {
            digits: remaining,
            countryDialCodeId: country.id,
            e164: `+${country.dial_code}${remaining}`,
          };
        }
      }
    }

    // Has + prefix but no match - return digits with no country
    const digits = trimmed.replace(/\D/g, "");
    return { digits, countryDialCodeId: null, e164: digits ? `+${digits}` : null };
  }

  // No + prefix - check if it looks like a US number
  const digits = trimmed.replace(/\D/g, "");

  if (digits.length === 10) {
    // 10 digits -> assume US
    return {
      digits,
      countryDialCodeId: US_COUNTRY_ID,
      e164: `+1${digits}`,
    };
  }

  if (digits.length === 11 && digits.startsWith("1")) {
    // 11 digits starting with 1 -> US with country code
    return {
      digits: digits.slice(1),
      countryDialCodeId: US_COUNTRY_ID,
      e164: `+${digits}`,
    };
  }

  // Unknown format - return digits only
  return {
    digits,
    countryDialCodeId: null,
    e164: digits.length >= 7 ? `+${digits}` : null,
  };
}

/**
 * Split a possibly comma-separated stored phone field into every distinct number, normalized
 * to E.164. Some tables (e.g. `venue_static.phone`) store multiple numbers in one column —
 * `"+1 212-465-2000,6467708700"` — rather than one number per row; a caller that only reads
 * the raw string will miss every number after the first. A part that doesn't normalize to a
 * valid number is skipped rather than throwing.
 *
 * @param raw - The raw stored value, or null/undefined.
 * @returns Distinct valid E.164 numbers, de-duped, in first-seen order.
 */
export function splitStoredPhoneList(raw: string | null | undefined): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(",")) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const e164 = normalizeExistingPhone(trimmed).e164;
    if (e164 && !seen.has(e164)) {
      seen.add(e164);
      out.push(e164);
    }
  }
  return out;
}

/**
 * Checks if an E.164 phone number is a US or Canada number (starts with +1).
 * @param e164 - Phone number in E.164 format
 * @returns true if the number starts with +1
 */
export function isUSOrCanadaNumber(e164: string): boolean {
  return e164.startsWith("+1");
}

/**
 * Short admin-facing label for blast routing: US/Canada (+1) vs international country code.
 * @param e164 - Normalized E.164 including leading `+`.
 */
export function e164RegionLabel(e164: string): string {
  if (isUSOrCanadaNumber(e164)) return "US/Canada (+1)";
  const match = e164.match(/^\+(\d{1,4})/);
  const cc = match?.[1];
  return cc ? `International (+${cc})` : "International";
}

/**
 * Formats a stored national phone number for display with its country code,
 * e.g. `"91288777"` + SG → `"+65 9128 8777"`.
 *
 * Assumes `phone` is already the national number (no dial code baked in) —
 * normalization to national form happens server-side at the write/load
 * boundaries (`@/lib/phone/phone-normalize`). This helper is client-safe: it
 * has no `libphonenumber-js` dependency, so it's the one to use inside
 * `"use client"` components. Falls back to the raw value when it can't build
 * an E.164, so a number is never hidden.
 *
 * @param phone - National-number digits as stored (e.g. `"91288777"`).
 * @param countryDialCodeId - FK into `countries_dial_codes` for the country code.
 * @returns Display string like `"+65 9128 8777"`, or `""` when no phone.
 */
export function formatStoredPhoneForDisplay(
  phone: string | null | undefined,
  countryDialCodeId: number | null
): string {
  if (!phone) return "";
  const e164 = toE164(phone, countryDialCodeId);
  return e164 ? formatE164ForDisplay(e164) : phone;
}

/**
 * Finds a CountryDialCode by matching a dial code string (e.g., "1", "44").
 */
export function findCountryByDialCode(dialCode: string): CountryDialCode | null {
  const countries = getCountriesDialCodes();
  return countries.find((c) => c.dial_code === dialCode) || null;
}

/**
 * Formats an E.164 phone number for human display.
 * - US/Canada (+1): `+1 (XXX) XXX-XXXX`
 * - Other:          `+CC XXX XXX XXXX` (national digits chunked in groups of 3–4)
 *
 * Falls back to the input string if it doesn't parse as E.164.
 *
 * @param e164 - Phone number in E.164 format (e.g. `+14155551234`).
 * @returns A pretty-printed phone number, or the input unchanged if unparseable.
 */
export function formatE164ForDisplay(e164: string | null | undefined): string {
  if (!e164) return "";
  const trimmed = e164.trim();

  // Legacy rows may be missing the leading `+` — recover the common 10- or 11-digit US case.
  if (!trimmed.startsWith("+")) {
    const allDigits = trimmed.replace(/\D/g, "");
    if (allDigits.length === 10) {
      return `+1 (${allDigits.slice(0, 3)}) ${allDigits.slice(3, 6)}-${allDigits.slice(6)}`;
    }
    if (allDigits.length === 11 && allDigits.startsWith("1")) {
      const national = allDigits.slice(1);
      return `+1 (${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
    }
    return trimmed;
  }

  // US/Canada fast path: +1 XXX XXX XXXX
  if (trimmed.startsWith("+1")) {
    const national = trimmed.slice(2).replace(/\D/g, "");
    if (national.length === 10) {
      return `+1 (${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
    }
    return trimmed;
  }

  // International: detect dial code via known list, then chunk the national number.
  for (const country of DIAL_CODES_LONGEST_FIRST) {
    const prefix = `+${country.dial_code}`;
    if (trimmed.startsWith(prefix)) {
      const national = trimmed.slice(prefix.length).replace(/\D/g, "");
      if (!national) return trimmed;
      const chunks = national.match(/\d{1,4}(?=(\d{4})+$)|\d{1,4}$/g) ?? [national];
      return `+${country.dial_code} ${chunks.join(" ")}`;
    }
  }

  return trimmed;
}
